<template>
  <div class="border rounded shadow bg-white h-100">
    <LmComponentLoading v-if="isLoading" />
    <div class="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
      <i class="mdi" :class="icon" style="font-size: 1.5rem"></i>
      <h4 class="text-center m-0">{{ title }}</h4>
      <span class="clickable" @click="$emit('close')">
        <i class="mdi mdi-close" style="font-size: 1.5rem"></i>
      </span>
    </div>
    <section class="py-2 px-3">
      <dropdown-menu
        v-model="showDropdown"
        interactive
      >
      <button class="toggle text-nowrap">
        Nueva
        <i class="mdi mdi-plus"></i>
      </button>
      <div slot="dropdown">
        <div class="dropdown-options">
          <b-form-input
            class="dropdown-item searcher"
            size="sm"
            placeholder="Buscar o crear"
            v-model="search"
            @keypress.enter="onCreateTag"
          ></b-form-input>
          <div class="dropdown-options-container" v-if="tagsFiltered.length">
            <button
              v-for="(item, index) in tagsFiltered"
              :key="index"
              class="dropdown-item"
              :class="{ 'selected': item.selected }"
              :disabled="isProcessing"
              @click="onClick(item)"
            >
              <span class="d-flex justify-content-between">
                <div class="d-flex align-items-center">
                  <i class="mdi mdi-dots-vertical mr-2" @click.stop :id="`toolbar-tag-${index}`"></i>
                  <b-popover
                    :target="`toolbar-tag-${index}`"
                    triggers="hover focus"
                    placement="left"
                    boundary="viewport"
                  >
                    <span
                      class="text-danger d-flex align-items-center clickable"
                      @click="onDeleteTag(item)"
                    >
                      <i class="mdi mdi-delete-outline mr-2"></i>
                      Borrar
                    </span>
                    <div class="tag-colors mt-3">
                      <span
                        v-for="(color, index) in tagsColors"
                        :key="index"
                        :style="`background-color: ${color}`"
                        :class="{ 'selected': tagColorSelected(item, color) }"
                        @click="onChangeTagColor(item, color)"
                      ></span>
                    </div>
                  </b-popover>
                  <span class="dropdown-item-label">{{ item.name }}</span>
                </div>
                <i class="mdi mdi-check" v-if="item.selected"></i>
              </span>
            </button>
          </div>
          <p class="p-2 text-muted" v-else>No encontramos un tag con este nombre, puedes crearlo presionando enter.</p>
        </div>
      </div>
      </dropdown-menu>

      <section class="py-4 d-flex flex-wrap" style="gap: 10px" v-if="!isProcessing">
        <b-badge
          v-for="(item, index) in currentTags"
          :key="index"
          pill
          class="p-1 px-2"
          :style="`background-color: ${item.color}`"
        >
          <span
            role="textbox"
            contenteditable
            @keypress.enter.prevent="onUpdateTag(item, index, $event)"
            class="tag-input-text mr-2"
          >{{ item.name }}</span>
          <span class="clickable" @click="onRemoveTag(item, index)"><i class="mdi mdi-close"></i></span>
        </b-badge>
      </section>
    </section>
  </div>
</template>

<script>
import DropdownMenu from '@innologica/vue-dropdown-menu'
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'

export default {
  mixins: [lmToasts],
  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    serviceName: {
      type: String,
      required: true
    }
  },
  components: {
    DropdownMenu
  },
  data () {
    return {
      isLoading: false,
      isProcessing: false,
      showDropdown: false,
      search: '',
      tags: [],
      currentTags: [],
      tagsColors: ['#546e7a', '#c43c00', '#33691e', '#0d47a1', '#c62828', '#7b1fa2']
    }
  },
  computed: {
    tagsFiltered () {
      return this.tags.filter(el => {
        return el.name.toLowerCase().search(this.search.toLowerCase()) >= 0
      })
    },
    tagsEndpoing () {
      return `/${this.serviceName}/${this.$route.params.id}/tags`
    }
  },
  methods: {
    mapTag (tag) {
      return {
        id: tag._id || tag.id,
        name: tag.name,
        color: tag.color
      }
    },
    async loadTags () {
      const res = await commonsApi.axiosGetWrapper(`/tags/${this.serviceName}/all`)
      this.tags = res.data.map(this.mapTag)
    },
    async loadData () {
      const res = await commonsApi.axiosGetWrapper(this.tagsEndpoing)
      this.currentTags = res.data.tags.map(this.mapTag)
    },
    loadPickedTags () {
      this.tags.forEach(item => {
        if (this.currentTags.some(el => el.id === item.id)) {
          item.selected = true
        } else {
          item.selected = false
        }
      })
    },
    async onRemoveTag (item) {
      await commonsApi.axiosPutWrapper(`/${this.serviceName}/remove-tag`, {
        id: item.id,
        docId: this.$route.params.id
      })
      this.currentTags = this.currentTags.filter(el => el.id !== item.id)
      this.loadPickedTags()
    },
    async onDeleteTag (item) {
      await this.onRemoveTag(item)
      await commonsApi.axiosPutWrapper(`/tags/delete`, {
        id: item.id
      })
      this.currentTags = this.currentTags.filter(el => el.id !== item.id)
      this.tags = this.tags.filter(el => el.id !== item.id)
    },
    async onAddTag (id) {
      const res = await commonsApi.axiosPostWrapper(`/${this.serviceName}/add-tag`, {
        id,
        docId: this.$route.params.id
      })
      const tag = this.mapTag(res.data)
      this.currentTags.push(tag)
      this.loadPickedTags()
    },
    async onClick (item) {
      this.isProcessing = true
      if (item.selected) {
        await this.onRemoveTag(item)
      } else {
        await this.onAddTag(item.id)
      }
      this.isProcessing = false
    },
    async onUpdateTag (item, index, event) {
      const res = await commonsApi.axiosPutWrapper(`/${this.serviceName}/update-tag`, {
        id: item.id,
        name: event?.target?.textContent || event,
        color: item.color || this.tagsColors.at(0)
      })
      const tag = this.mapTag(res.data)
      if (index >= 0) {
        this.currentTags[index] = tag
      }
      this.tags.forEach(el => {
        if (el.id === tag.id) {
          el.name = tag.name
          el.color = tag.color
        }
      })
      this.lmToastSuccessMessage('Se guardó correctamente', 'Acción realizada con éxito')
    },
    async onChangeTagColor (item, color) {
      this.isProcessing = true
      item.color = color
      const index = this.currentTags.findIndex(el => el.id === item.id)
      await this.onUpdateTag(item, index, item.name)
      this.isProcessing = false
    },
    tagColorSelected (item, color) {
      return item.color === color
    },
    async onCreateTag () {
      const res = await commonsApi.axiosPostWrapper(`/${this.serviceName}/create-tag`, {
        id: this.$route.params.id,
        name: this.search,
        color: this.tagsColors.at(Math.floor(Math.random() * this.tagsColors.length))
      })
      const tag = this.mapTag(res.data)
      this.tags.push(tag)
      this.currentTags.push(tag)
      this.loadPickedTags()
      this.lmToastSuccessMessage('Se creó correctamente', 'Acción realizada con éxito')
    }
  },
  watch: {
    showDropdown () {
      this.search = ''
    }
  },
  async created () {
    try {
      this.isLoading = true
      await Promise.all([this.loadTags(), this.loadData()])
      this.loadPickedTags()
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>

<style lang="sass" scoped>
  .tag-input-text
    outline: 0
    background: transparent
    border: 0
    width: 100%
  .tag-colors
    display: flex
    gap: 5px
    span
      border-radius: 50%
      height: 12px
      width: 12px
      &.selected
        border: 1px solid white
        outline: 1px solid black
  button.toggle
    display: flex
    gap: 10px
    background-color: #e9ecef
    border: 0
    padding: .5rem .8rem
    border-radius: 8px
    .toggle-count
      background-color: white
      display: flex
      justify-content: center
      align-items: center
      width: 21px
      height: 21px
      border-radius: 50%
  .dropdown-options
    .dropdown-options-container
      max-height: 400px
      overflow-y: auto
      margin: 2px 0
      &:hover
        &::-webkit-scrollbar-thumb
          border-radius: 20px
          background: #888
      &::-webkit-scrollbar
        width: 8px
      &::-webkit-scrollbar-thumb
        border-radius: 20px
        background: #888
      &::-webkit-scrollbar-thumb:hover
        background: #555
    .dropdown-item
      &.searcher
        background-color: #e9ecef
        margin-bottom: .5rem
        &:focus
          box-shadow: none
      &:active
        background-color: #e9ecef
        color: #212529
      &.selected
        background-color: #e9ecef
        .dropdown-item-label
          margin-right: 15px
      .dropdown-item-label
          margin-right: 31px
      i.mdi
        height: 24px
</style>
